import { CaretUp } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';

type TFunction = {
  (key: string): string;
  rich: (
    key: string,
    values: Record<string, (chunks: React.ReactNode) => React.ReactNode>,
  ) => React.ReactNode;
};

const textItem = (t: TFunction, id: string) => ({
  title: t(`faq.${id}.title`),
  description: <>{t(`faq.${id}.body`)}</>,
});

const fingerGuide = (chunks: React.ReactNode) => (
  <CustomLink
    href="https://www.notion.so/22aa9960c9b649dda983ca21bfa5ac34?pvs=21"
    target={'_blank'}
  >
    {chunks}
  </CustomLink>
);

/**
 * /diesel-ultrahuman-ring/buy FAQ. `t` is dieselRing, `tRing` is ring
 * (shared question titles + "See all FAQs").
 */
export const getDieselRingBuyPageFaqData = (t: TFunction, tRing: TFunction) => [
  {
    title: tRing('ring.faq.data.titleA'),
    description: <>{t('faq.sizeChoice.body')}</>,
  },
  textItem(t, 'sizes'),
  {
    title: t('faq.costHigher.title'),
    description: (
      <>
        <p>{t('faq.costHigher.p1')}</p>
        <br />
        <p>{t('faq.costHigher.p2')}</p>
      </>
    ),
  },
  {
    title: t('faq.finger.title'),
    description: <>{t.rich('faq.finger.body', { guide: fingerGuide })}</>,
  },
  textItem(t, 'sizeCompare'),
  {
    title: tRing('ring.faq.data.titleB'),
    description: <>{t('faq.orderNext.body')}</>,
  },
  {
    title: tRing('ring.faq.data.titleC'),
    description: <>{t('faq.subscription.body')}</>,
  },
  {
    title: ' ',
    description: (
      <>
        <CustomLink href="/diesel-ultrahuman-ring/faq">
          {tRing('ring.faq.seeAll')}{' '}
          <CaretUp
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];

/**
 * /diesel-ultrahuman-ring/faq data. `t` is the dieselRing namespace, `tRing` the
 * ring namespace (shared question titles). COD is not offered in India, so the
 * COD answer is dropped there; every other region keeps it.
 */
export const getDieselRingFaqData = (
  t: TFunction,
  tRing: TFunction,
  isIndia: boolean,
) => [
  {
    title: tRing('ring.faq.data.titleA'),
    description: <>{t('faq.sizeChoice.body')}</>,
  },
  textItem(t, 'sizes'),
  {
    title: t('faq.finger.title'),
    description: <>{t.rich('faq.finger.body', { guide: fingerGuide })}</>,
  },
  textItem(t, 'sizeCompare'),
  {
    title: tRing('ring.faq.data.titleB'),
    description: <>{t('faq.orderNext.body')}</>,
  },
  {
    title: tRing('ring.faq.data.titleC'),
    description: <>{t('faq.subscription.body')}</>,
  },
  ...(isIndia ? [] : [textItem(t, 'cod')]),
  textItem(t, 'international'),
  {
    title: t('faq.uhx.title'),
    description: (
      <>
        <strong>{t('faq.uhx.plan1Title')}</strong>
        <br />
        <br />
        <ul>
          <li>{t('faq.uhx.plan1Accidental')}</li>
          <li>{t('faq.uhx.plan1Priority')}</li>
        </ul>
        <br />
        <strong>{t('faq.uhx.plan2Title')}</strong>
        <br />
        <br />
        <ul>
          <li>{t('faq.uhx.plan2Includes')}</li>
          <li>{t('faq.uhx.plan2Theft')}</li>
          <li>{t('faq.uhx.plan2WeightLoss')}</li>
        </ul>
      </>
    ),
  },
  {
    title: tRing('ring.faq.data.titleE'),
    description: <>{t('faq.colours.body')}</>,
  },
  textItem(t, 'battery'),
  textItem(t, 'devices'),
  textItem(t, 'material'),
  textItem(t, 'sensors'),
  textItem(t, 'hrv'),
  textItem(t, 'appFeatures'),
  textItem(t, 'parameters'),
  textItem(t, 'm1Metabolic'),
  textItem(t, 'm1Sleep'),
  {
    title: t('faq.vsRingAir.title'),
    description: (
      <>
        {t('faq.vsRingAir.p1')}
        <br />
        <br />
        {t('faq.vsRingAir.p2')}
        <br />
        <br />
        {t('faq.vsRingAir.p3')}
      </>
    ),
  },
  textItem(t, 'vsWearables'),
  textItem(t, 'vsM1'),
  textItem(t, 'accuracy'),
  textItem(t, 'cgm'),
  textItem(t, 'dailyWear'),
  textItem(t, 'workout'),
  textItem(t, 'underwater'),
  textItem(t, 'calibration'),
  textItem(t, 'chargeTime'),
  textItem(t, 'sharing'),
  textItem(t, 'skin'),
  {
    title: t('faq.stuck.title'),
    description: (
      <>
        {t('faq.stuck.intro')}
        <br />
        <br />
        {t('faq.stuck.tryIntro')}
        <ul>
          <li>{t('faq.stuck.tryColdWater')}</li>
          <li>{t('faq.stuck.trySoap')}</li>
          <li>{t('faq.stuck.tryTwist')}</li>
          <li>{t('faq.stuck.tryRaiseHand')}</li>
          <li>{t('faq.stuck.tryWait')}</li>
        </ul>
        <br />
        <b>{t('faq.stuck.stillStuckLabel')}</b> {t('faq.stuck.stillStuckBody')}
        <ul>
          <li>{t('faq.stuck.cutNoSelf')}</li>
          <li>{t('faq.stuck.cutBattery')}</li>
          <li>{t('faq.stuck.cutWiden')}</li>
          <li>{t('faq.stuck.cutDispose')}</li>
        </ul>
      </>
    ),
  },
  textItem(t, 'emf'),
  textItem(t, 'medicalDevice'),
  textItem(t, 'pacemaker'),
  textItem(t, 'esd'),
  textItem(t, 'tattoo'),
  textItem(t, 'safetyDesign'),
  textItem(t, 'disposal'),
  textItem(t, 'warranty'),
  textItem(t, 'returns'),
  textItem(t, 'returnShipping'),
  textItem(t, 'cancellation'),
  {
    title: t('faq.termsOfSale.title'),
    description: (
      <>
        {t.rich('faq.termsOfSale.body', {
          link: (chunks) => (
            <CustomLink
              href="https://www.ultrahuman.com/termsOfSale/UltrahumanRing/"
              target={'_blank'}
            >
              {chunks}
            </CustomLink>
          ),
        })}
      </>
    ),
  },
];
